import { useEffect } from "react";
import { Routes, Route, Link, NavLink, useLocation, matchPath } from "react-router-dom";
import { Dashboard } from "./pages/Dashboard";
import { WalletDetail } from "./pages/WalletDetail";
import { Search } from "./pages/Search";
import { Machines } from "./pages/Machines";
import { FreshSpinners } from "./pages/FreshSpinners";
import { Leaderboards } from "./pages/Leaderboards";
import { Airdrops } from "./pages/Airdrops";
import { SearchBar } from "./components/SearchBar";

const NAV = [
  { to: "/", label: "Overview", end: true },
  { to: "/machines", label: "Machines" },
  { to: "/fresh-spinners", label: "Fresh spinners" },
  { to: "/leaderboards", label: "Leaderboards" },
  { to: "/airdrops", label: "$CARDS airdrops" },
];

/** Brand mark: three fanned cards, the gold one on top. */
function Logo() {
  return (
    <svg
      viewBox="0 0 32 32"
      className="w-8 h-8"
      aria-hidden="true"
      fill="none"
    >
      <rect
        x="4"
        y="9"
        width="13"
        height="18"
        rx="2.5"
        transform="rotate(-14 10.5 18)"
        className="fill-gray-600"
      />
      <rect
        x="10"
        y="7"
        width="13"
        height="18"
        rx="2.5"
        transform="rotate(-4 16.5 16)"
        className="fill-blue-500"
      />
      <rect
        x="16"
        y="6"
        width="13"
        height="18"
        rx="2.5"
        transform="rotate(8 22.5 15)"
        className="fill-yellow-400"
      />
      <rect
        x="19.5"
        y="9.5"
        width="6"
        height="4.5"
        rx="1"
        transform="rotate(8 22.5 15)"
        className="fill-gray-900/70"
      />
    </svg>
  );
}

const PAGE_TITLES: { path: string; title: string }[] = [
  { path: "/", title: "Overview" },
  { path: "/machines", title: "Machines" },
  { path: "/fresh-spinners", title: "Fresh spinners" },
  { path: "/leaderboards", title: "Leaderboards" },
  { path: "/airdrops", title: "$CARDS airdrops" },
  { path: "/search", title: "Search" },
];

/** Scroll to the top on navigation and keep the tab title in sync with the page. */
function RouteEffects() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0 });
    const wallet = matchPath("/wallet/:address", pathname)?.params.address;
    const page = wallet
      ? `Wallet ${wallet.slice(0, 4)}…${wallet.slice(-4)}`
      : PAGE_TITLES.find((p) => p.path === pathname)?.title;
    document.title = page ? `${page} · CC Tracker` : "CC Tracker";
  }, [pathname]);
  return null;
}

function App() {
  return (
    <div className="min-h-screen flex flex-col">
      <RouteEffects />
      <header className="sticky top-0 z-20 border-b border-gray-700/70 bg-gray-900/85 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-4 py-3">
            <Link to="/" className="flex items-center gap-2.5 shrink-0">
              <Logo />
              <span className="font-semibold text-gray-50 tracking-tight leading-none">
                CC Tracker
                <span className="block text-[11px] font-normal text-gray-500 mt-0.5">
                  gacha stats, independent
                </span>
              </span>
            </Link>
            <nav className="hidden lg:flex items-center gap-1 ml-4">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `nav-link ${isActive ? "nav-link-active" : ""}`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <div className="flex-1" />
            <div className="w-full max-w-md hidden sm:block">
              <SearchBar placeholder="Wallet address or refcode" />
            </div>
          </div>
          {/* Mobile: search + scrolling nav */}
          <div className="sm:hidden pb-3">
            <SearchBar placeholder="Wallet address or refcode" />
          </div>
          <nav className="lg:hidden flex gap-1 overflow-x-auto pb-2 -mx-1 px-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `nav-link ${isActive ? "nav-link-active" : ""}`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>

      <main className="max-w-7xl w-full mx-auto px-4 py-8 flex-1">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/wallet/:address" element={<WalletDetail />} />
          <Route path="/search" element={<Search />} />
          <Route path="/machines" element={<Machines />} />
          <Route path="/fresh-spinners" element={<FreshSpinners />} />
          <Route path="/leaderboards" element={<Leaderboards />} />
          <Route path="/airdrops" element={<Airdrops />} />
        </Routes>
      </main>

      <footer className="border-t border-gray-700/70">
        <div className="max-w-7xl mx-auto px-4 py-5 flex flex-wrap items-center justify-between gap-2 text-sm text-gray-500">
          <span>CC Tracker is an independent fan project. Not affiliated with Collector Crypt.</span>
          <span>Prize values are insured values reported by the gacha, in USD.</span>
        </div>
      </footer>
    </div>
  );
}

export default App;
